import { useState, useEffect } from 'react';

export interface OfflineCapabilities {
  isOnline: boolean;
  isOffline: boolean;
  wasOffline: boolean;
  lastOnlineAt: Date | null;
  saveForLater: (key: string, data: any) => void;
  getOfflineData: (key: string) => any;
  clearOfflineData: (key?: string) => void;
  syncWhenOnline: () => Promise<void>;
}

export const useOffline = (): OfflineCapabilities => {
  // Start as online to match the server-rendered (prerendered) HTML exactly;
  // the real navigator.onLine value is synced in the mount effect below.
  const [isOnline, setIsOnline] = useState(true);
  const [wasOffline, setWasOffline] = useState(false);
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(
    () => new Date()
  );

  useEffect(() => {
    // Sync the actual connectivity state after hydration. setState bails out
    // when the value is unchanged (the common, online case).
    setIsOnline(navigator.onLine);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastOnlineAt(new Date());
      if (wasOffline) {
        // Trigger sync when coming back online
        syncWhenOnline();
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  const saveForLater = (key: string, data: any): void => {
    try {
      const offlineData = {
        data,
        timestamp: new Date().toISOString(),
        synced: false
      };
      localStorage.setItem(`offline_${key}`, JSON.stringify(offlineData));
    } catch (error) {
      console.error('Failed to save offline data:', error);
    }
  };

  const getOfflineData = (key: string): any => {
    try {
      const item = localStorage.getItem(`offline_${key}`);
      if (item) {
        const parsed = JSON.parse(item);
        return parsed.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to get offline data:', error);
      return null;
    }
  };

  const clearOfflineData = (key?: string): void => {
    try {
      if (key) {
        localStorage.removeItem(`offline_${key}`);
      } else {
        // Clear all offline data
        Object.keys(localStorage)
          .filter(k => k.startsWith('offline_'))
          .forEach(k => localStorage.removeItem(k));
      }
    } catch (error) {
      console.error('Failed to clear offline data:', error);
    }
  };

  const syncWhenOnline = async (): Promise<void> => {
    if (!isOnline) {
      return;
    }

    try {
      const offlineItems = Object.keys(localStorage)
        .filter(key => key.startsWith('offline_'))
        .map(key => {
          const item = localStorage.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            return {
              key: key.replace('offline_', ''),
              ...parsed
            };
          }
          return null;
        })
        .filter(Boolean);


      for (const item of offlineItems) {
        if (!item || item.synced) continue;

        try {
          // Attempt to sync the specific item
          await syncOfflineItem(item);
          
          // Mark as synced
          const updatedItem = { ...item, synced: true };
          localStorage.setItem(`offline_${item.key}`, JSON.stringify(updatedItem));
          
        } catch (error) {
          console.error('Failed to sync item:', item.key, error);
        }
      }

      // Clean up synced items after successful sync
      const syncedItems = offlineItems.filter(item => item?.synced);
      syncedItems.forEach(item => {
        if (item) {
          localStorage.removeItem(`offline_${item.key}`);
        }
      });

    } catch (error) {
      console.error('Sync failed:', error);
    }
  };

  const syncOfflineItem = async (item: any): Promise<void> => {
    // Handle different types of offline data
    switch (item.key) {
      case 'test_answers':
        await syncTestAnswers(item.data);
        break;
      case 'user_profile':
        await syncUserProfile(item.data);
        break;
      case 'module_progress':
        await syncModuleProgress(item.data);
        break;
      default:
    }
  };

  const syncTestAnswers = async (data: any): Promise<void> => {
    // Sync test answers to server
    const response = await fetch('/api/questions/test/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to sync test answers');
    }
  };

  const syncUserProfile = async (data: any): Promise<void> => {
    // Sync user profile changes
    const response = await fetch('/api/auth/profile/sync', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to sync user profile');
    }
  };

  const syncModuleProgress = async (data: any): Promise<void> => {
    // Sync module progress
    const response = await fetch('/api/progress/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to sync module progress');
    }
  };

  return {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
    lastOnlineAt,
    saveForLater,
    getOfflineData,
    clearOfflineData,
    syncWhenOnline
  };
};